import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Users,
  Star,
  Zap,
  ArrowRight,
  Gift,
  MessageCircle,
  Send,
  ChevronDown,
  ChevronUp,
  Trophy,
  Target,
  Eye,
  BarChart3,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { wheelsApi } from '../../services/api';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { SubscriptionInfo } from '../../types';
import { KIWIFY_PRODUCT_URL, KIWIFY_PRODUCT_URL_ANNUAL } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { LoadingState } from '../../components/LoadingSpinner';
import WheelIcon from '../../components/icons/WheelIcon';

interface BirthdayLead {
  id: number;
  customer_name: string;
  whatsapp: string;
  customer_birthdate: string;
  wheel_name: string;
  prize_name: string;
  created_at: string;
}

interface DashboardStats {
  dailySpins: Array<{ date: string; total: number; winners: number }>;
  topPrizes: Array<{ name: string; total: number }>;
  activeWheels: Array<{ id: number; name: string; slug: string; spinsWeek: number; totalSpins: number }>;
  stockAlerts: Array<{ product_name: string; stock: number; wheel_name: string; wheel_id: number }>;
  inactiveWheels: Array<{ id: number; name: string }>;
  metrics: {
    spinsToday: number;
    spinsThisWeek: number;
    spinsLastWeek: number;
    spinsThisMonth: number;
    spinsLastMonth: number;
    conversionRate: number;
    uniqueLeadsMonth: number;
  };
}

const DashboardPage: React.FC = () => {
  usePageTitle('Dashboard');
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [birthdayLeads, setBirthdayLeads] = useState<BirthdayLead[]>([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(true);
  const [showBirthdayBanner, setShowBirthdayBanner] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    loadDashboardData();
    loadSubscriptionInfo();
    loadBirthdayLeads();

    const hiddenBirthday = localStorage.getItem('hideBirthdayBanner');
    if (hiddenBirthday === 'true') {
      setShowBirthdayBanner(false);
    }
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      setLoadingSubscription(true);
      const response = await api.get<SubscriptionInfo>('/subscription/status');
      setSubscription(response.data);
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const loadBirthdayLeads = async () => {
    try {
      setLoadingBirthdays(true);
      const response = await api.get('/wheels/spins/birthdays', {
        params: { page: 1, limit: 50 }
      });
      setBirthdayLeads(response.data.spins || []);
    } catch (error: any) {
      console.error('Error fetching birthdays:', error);
    } finally {
      setLoadingBirthdays(false);
    }
  };

  const handleSendBirthdayMessage = useCallback((lead: BirthdayLead) => {
    const message = encodeURIComponent(
      `Parabéns, ${lead.customer_name}! \n\n` +
      `Hoje é um dia especial e queremos celebrar com você! \n\n` +
      `Desejamos um feliz aniversário repleto de alegrias, conquistas e momentos inesquecíveis! \n\n` +
      `Que este novo ano de vida seja incrível!` +
      `Um grande abraço da Equipe ${user?.company_name}`
    );

    const whatsapp = lead.whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/${whatsapp}?text=${message}`, '_blank');
  }, [user]);

  const toggleBirthdayBanner = () => {
    const newState = !showBirthdayBanner;
    setShowBirthdayBanner(newState);
    localStorage.setItem('hideBirthdayBanner', String(!newState));
  };

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const data = await wheelsApi.getDashboardStats();
      setStats(data);
    } catch (error: any) {
      toast.error(error.error || 'Erro ao carregar dados do dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (previous === 0 && current === 0) return null;
    if (current >= previous) {
      return <TrendingUp className="h-4 w-4 text-musgo" />;
    }
    return <TrendingDown className="h-4 w-4 text-bordo" />;
  };

  const getTrendPercent = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const diff = ((current - previous) / previous) * 100;
    return `${diff >= 0 ? '+' : ''}${diff.toFixed(0)}%`;
  };

  const getTrendColor = (current: number, previous: number) => {
    if (current >= previous) return 'text-musgo';
    return 'text-bordo';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <LoadingState message="Carregando dados do dashboard..." />
      </div>
    );
  }

  const m = stats?.metrics;
  const maxDailySpins = Math.max(...(stats?.dailySpins?.map(d => d.total) || [0]), 1);

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <div className="bg-white border border-tinta/10 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-bordo/10 border border-bordo/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-bordo" />
              </div>
            </div>
            <div className="ml-4">
              <h1 className="font-display text-3xl font-black text-tinta leading-tight">
                Bem-vindo{user?.name ? <>, <span className="italic text-bordo">{user.name.split(' ')[0]}</span></> : ''}!
              </h1>
              <p className="text-tinta-soft mt-1">
                Acompanhe o desempenho das suas rodas de prêmios em tempo real
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Free Plan Upgrade Banner */}
      {!loadingSubscription && subscription && subscription.planName === 'Free' && (
        <div className="bg-bordo rounded-xl shadow-sm overflow-hidden border border-tinta/15">
          <div className="px-6 py-8 sm:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-mostarda border border-tinta/30 mr-3">
                    <Star className="h-5 w-5 text-tinta" fill="currentColor" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-creme">
                    Desbloqueie todo o <span className="italic text-mostarda">potencial</span>!
                  </h3>
                </div>
                <p className="text-creme/90 text-base mb-4">
                  Você está no <span className="font-bold">Plano Gratuito</span>. Faça upgrade para o <span className="font-bold text-mostarda">Plano Pro</span> e tenha:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  {['Roda de prêmios ilimitadas', 'Produtos ilimitados', 'Integração com WhatsApp', 'Giros ilimitados por mês', 'Sem propagandas'].map((item) => (
                    <div key={item} className="flex items-center text-creme">
                      <Zap className="h-4 w-4 mr-2 text-mostarda flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-display text-3xl font-black text-mostarda">R$ 9,90</span>
                  <span className="text-creme/75">/mês</span>
                </div>
              </div>
              <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <a
                  href={KIWIFY_PRODUCT_URL}
                  target='_blank'
                  rel='noreferrer'
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-creme text-sm font-bold rounded-lg text-creme bg-transparent hover:bg-creme hover:text-bordo transition-colors"
                >
                  Mensal — R$ 9,90
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
                <a
                  href={KIWIFY_PRODUCT_URL_ANNUAL}
                  target='_blank'
                  rel='noreferrer'
                  className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold rounded-lg text-tinta bg-mostarda hover:bg-mostarda-light transition-colors border-2 border-mostarda"
                >
                  Anual — R$ 99,00
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Birthday Leads Banner */}
      {!loadingBirthdays && birthdayLeads.length > 0 && (
        <div className="bg-mostarda/20 border border-mostarda/50 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-4 sm:px-6 sm:py-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 lg:gap-6">
              <div className="flex-1 w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-bordo border border-tinta/20 mr-2 sm:mr-3">
                      <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-creme" />
                    </div>
                    <h3 className="font-display text-lg sm:text-2xl font-bold text-tinta">
                      {birthdayLeads.length} <span className="italic text-bordo">aniversariante{birthdayLeads.length > 1 ? 's' : ''}</span> hoje!
                    </h3>
                  </div>
                  <button
                    onClick={toggleBirthdayBanner}
                    className="flex-shrink-0 text-tinta-soft hover:text-bordo transition-colors p-2 hover:bg-mostarda/30 rounded-lg"
                    title={showBirthdayBanner ? 'Esconder aniversariantes' : 'Mostrar aniversariantes'}
                  >
                    {showBirthdayBanner ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {showBirthdayBanner && (
                  <div className='mt-3'>
                    <p className="text-tinta-soft text-sm sm:text-base mb-3 sm:mb-4">
                      Que tal enviar uma mensagem especial para quem está fazendo aniversário hoje?
                    </p>
                    <div className="space-y-2 mb-4">
                      {birthdayLeads.slice(0, 3).map((lead) => (
                        <div
                          key={lead.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between bg-white border border-tinta/10 rounded-lg px-3 sm:px-4 py-2 sm:py-3 gap-2 sm:gap-0"
                        >
                          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-bordo" />
                              </div>
                            </div>
                            <div className="text-tinta min-w-0 flex-1">
                              <p className="font-bold text-sm sm:text-base truncate">{lead.customer_name}</p>
                              <p className="text-xs sm:text-sm text-tinta-soft truncate">{lead.whatsapp}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleSendBirthdayMessage(lead)}
                            className="flex items-center justify-center space-x-2 bg-bordo text-creme px-3 sm:px-4 py-2 rounded-lg text-sm font-bold hover:bg-bordo-dark transition-colors w-full sm:w-auto flex-shrink-0"
                          >
                            <MessageCircle className="h-4 w-4" />
                            <span>Parabenizar</span>
                          </button>
                        </div>
                      ))}
                      {birthdayLeads.length > 3 && (
                        <p className="text-tinta-soft text-xs sm:text-sm text-center py-2 italic">
                          + {birthdayLeads.length - 3} aniversariante{birthdayLeads.length - 3 > 1 ? 's' : ''}
                        </p>
                      )}
                      <div className="flex-shrink-0 flex flex-col sm:flex-row lg:flex-row gap-2 sm:gap-3 w-full pt-2">
                        <Link
                          to="/admin/whatsapp?tab=dispatch&source=birthdays"
                          className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-bold rounded-lg text-creme bg-bordo hover:bg-bordo-dark transition-colors"
                        >
                          <Send className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                          Enviar em Massa
                        </Link>
                        <Link
                          to="/admin/leads?filter=birthdays"
                          className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold rounded-lg text-tinta bg-white border border-tinta/15 hover:bg-creme-light hover:text-bordo transition-colors"
                        >
                          Ver Todos
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Metrics Cards */}
      {m && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Giros Hoje */}
          <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg bg-bordo/10 border border-bordo/20 flex items-center justify-center">
                <WheelIcon className="h-5 w-5 text-bordo" />
              </div>
              {getTrendIcon(m.spinsThisWeek, m.spinsLastWeek)}
            </div>
            <p className="font-display text-3xl font-black text-tinta">{m.spinsToday.toLocaleString()}</p>
            <p className="text-sm text-tinta-soft mt-1">Giros hoje</p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className={`text-xs font-bold ${getTrendColor(m.spinsThisWeek, m.spinsLastWeek)}`}>
                {getTrendPercent(m.spinsThisWeek, m.spinsLastWeek)}
              </span>
              <span className="text-xs text-tinta-soft/70">vs semana anterior</span>
            </div>
          </div>

          {/* Giros Mês */}
          <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg bg-musgo/10 border border-musgo/20 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-musgo" />
              </div>
              {getTrendIcon(m.spinsThisMonth, m.spinsLastMonth)}
            </div>
            <p className="font-display text-3xl font-black text-tinta">{m.spinsThisMonth.toLocaleString()}</p>
            <p className="text-sm text-tinta-soft mt-1">Giros este mês</p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className={`text-xs font-bold ${getTrendColor(m.spinsThisMonth, m.spinsLastMonth)}`}>
                {getTrendPercent(m.spinsThisMonth, m.spinsLastMonth)}
              </span>
              <span className="text-xs text-tinta-soft/70">vs mês anterior</span>
            </div>
          </div>

          {/* Taxa de Conversão */}
          <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg bg-mostarda/20 border border-mostarda/40 flex items-center justify-center">
                <Target className="h-5 w-5 text-mostarda-dark" />
              </div>
            </div>
            <p className="font-display text-3xl font-black text-tinta">{m.conversionRate}%</p>
            <p className="text-sm text-tinta-soft mt-1">Taxa de premiação</p>
            <p className="text-xs text-tinta-soft/70 mt-2">Giros que ganharam prêmio</p>
          </div>

          {/* Leads Capturados */}
          <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg bg-musgo/10 border border-musgo/20 flex items-center justify-center">
                <Users className="h-5 w-5 text-musgo" />
              </div>
            </div>
            <p className="font-display text-3xl font-black text-tinta">{m.uniqueLeadsMonth.toLocaleString()}</p>
            <p className="text-sm text-tinta-soft mt-1">Leads únicos (mês)</p>
            <Link to="/admin/leads" className="text-xs text-bordo hover:text-bordo-dark mt-2 inline-flex items-center gap-1 font-bold">
              Ver todos <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de Giros (14 dias) — 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-tinta-soft" />
              Giros por Dia
            </h3>
            <span className="text-xs text-tinta-soft italic">Últimos 14 dias</span>
          </div>

          {stats && stats.dailySpins.length > 0 ? (
            <div className="flex items-end gap-1.5" style={{ height: '160px' }}>
              {stats.dailySpins.map((day, i) => {
                const barHeight = Math.max(Math.round((day.total / maxDailySpins) * 140), 6);
                const winHeight = day.total > 0 ? Math.round((day.winners / day.total) * barHeight) : 0;
                const dateStr = typeof day.date === 'string' ? day.date.substring(0, 10) : new Date(day.date).toISOString().substring(0, 10);
                const parts = dateStr.split('-');
                const dayLabel = `${parts[2]}/${parts[1]}`;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" style={{ height: '100%' }}>
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-tinta text-creme text-xs px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none shadow-lg">
                      <p className="font-bold">{day.total} giros</p>
                      <p className="text-creme/80">{day.winners} prêmios</p>
                    </div>
                    <div
                      className="w-full max-w-[32px] rounded-t-md relative overflow-hidden transition-all duration-300 group-hover:opacity-80 cursor-pointer"
                      style={{ height: `${barHeight}px` }}
                    >
                      <div className="absolute inset-0 bg-bordo/20 rounded-t-md" />
                      <div
                        className="absolute bottom-0 inset-x-0 bg-bordo rounded-t-md transition-all duration-300"
                        style={{ height: `${winHeight}px` }}
                      />
                    </div>
                    <span className="text-[9px] text-tinta-soft mt-1.5 hidden sm:block whitespace-nowrap">
                      {dayLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-tinta-soft text-sm italic">
              Nenhum giro registrado nos últimos 14 dias
            </div>
          )}

          {stats && stats.dailySpins.length > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-tinta/10">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-bordo/20" />
                <span className="text-xs text-tinta-soft">Total de giros</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-bordo" />
                <span className="text-xs text-tinta-soft">Ganharam prêmio</span>
              </div>
            </div>
          )}
        </div>

        {/* Top Prêmios */}
        <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
          <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-mostarda-dark" />
            Top Prêmios (30 dias)
          </h3>

          {stats && stats.topPrizes.length > 0 ? (
            <div className="space-y-3">
              {stats.topPrizes.map((prize, i) => {
                const maxPrize = stats.topPrizes[0].total;
                const widthPercent = (prize.total / maxPrize) * 100;
                const barColor = i === 0 ? 'bg-mostarda'
                  : i === 1 ? 'bg-tinta-soft/40'
                    : i === 2 ? 'bg-bordo-dark'
                      : 'bg-bordo';
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-tinta truncate flex-1 mr-2 font-medium">
                        {i === 0 && '🥇 '}
                        {i === 1 && '🥈 '}
                        {i === 2 && '🥉 '}
                        {prize.name}
                      </span>
                      <span className="text-sm font-bold text-tinta">{prize.total}</span>
                    </div>
                    <div className="w-full h-2 bg-tinta/8 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-tinta-soft text-sm italic">
              Nenhum prêmio resgatado nos últimos 30 dias
            </div>
          )}
        </div>
      </div>

      {/* Second Row: Active Wheels + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rodas em Destaque */}
        <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
          <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-bordo" />
            Rodas Ativas
          </h3>

          {stats && stats.activeWheels.length > 0 ? (
            <div className="space-y-3">
              {stats.activeWheels.map((wheel) => (
                <a
                  key={wheel.id}
                  href={`https://participar.rodadepremios.com.br/w/${wheel.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between p-3 rounded-lg border border-tinta/10 hover:border-bordo/30 hover:bg-bordo/5 transition-all group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-tinta group-hover:text-bordo truncate transition-colors">
                      {wheel.name}
                    </p>
                    <p className="text-xs text-tinta-soft mt-0.5">
                      {wheel.totalSpins.toLocaleString()} giros no total
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <div className="text-right">
                      <p className="font-display text-sm font-bold text-bordo">{wheel.spinsWeek}</p>
                      <p className="text-[10px] text-tinta-soft">esta semana</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-tinta-soft/50 group-hover:text-bordo transition-colors" />
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-tinta-soft text-sm gap-2 italic">
              <p>Nenhuma roda ativa</p>
              <Link to="/admin/wheels" className="text-bordo hover:text-bordo-dark text-xs font-bold not-italic">
                Criar uma roda →
              </Link>
            </div>
          )}
        </div>

        {/* Alertas Inteligentes */}
        <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-5">
          <h3 className="font-display text-lg font-bold text-tinta flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-mostarda-dark" />
            Alertas e Atenção
          </h3>

          <div className="space-y-2.5">
            {/* Stock Alerts */}
            {stats?.stockAlerts?.map((alert, i) => (
              <div key={`stock-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-bordo/8 border border-bordo/20">
                <AlertCircle className="h-4 w-4 text-bordo mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-tinta">
                    <span className="font-bold">{alert.product_name}</span> com{' '}
                    {alert.stock === 0 ? (
                      <span className="font-bold text-bordo">estoque esgotado</span>
                    ) : (
                      <span>apenas <span className="font-bold">{alert.stock}</span> unidade{alert.stock !== 1 ? 's' : ''}</span>
                    )} na roda {alert.wheel_name}
                  </p>
                </div>
              </div>
            ))}

            {/* Inactive Wheels */}
            {stats?.inactiveWheels?.map((wheel, i) => (
              <div key={`inactive-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-mostarda/15 border border-mostarda/40">
                <AlertTriangle className="h-4 w-4 text-mostarda-dark mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-tinta">
                    <span className="font-bold">{wheel.name}</span> sem giros há 7+ dias — verifique se o link está visível
                  </p>
                </div>
              </div>
            ))}

            {/* Celebration */}
            {m && m.spinsToday >= 10 && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-musgo/10 border border-musgo/30">
                <CheckCircle className="h-4 w-4 text-musgo mt-0.5 flex-shrink-0" />
                <p className="text-sm text-tinta">
                  Excelente! <span className="font-bold">{m.spinsToday} giros</span> hoje — seu engajamento está ótimo!
                </p>
              </div>
            )}

            {/* System OK */}
            {(!stats?.stockAlerts || stats.stockAlerts.length === 0) &&
              (!stats?.inactiveWheels || stats.inactiveWheels.length === 0) &&
              (!m || m.spinsToday < 10) && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-musgo/10 border border-musgo/30">
                  <CheckCircle className="h-4 w-4 text-musgo mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-tinta">
                    Tudo funcionando perfeitamente! Nenhum alerta no momento.
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border border-tinta/10 shadow-sm rounded-xl">
        <div className="px-5 py-5">
          <h3 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
            <Zap className="h-5 w-5 text-mostarda-dark" />
            Ações Rápidas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { title: 'Nova Roda', icon: WheelIcon, link: '/admin/wheels', tint: 'bordo' },
              { title: 'Ver Leads', icon: Users, link: '/admin/leads', tint: 'musgo' },
              { title: 'Relatórios', icon: BarChart3, link: '/admin/reports', tint: 'mostarda' },
              { title: 'WhatsApp', icon: MessageCircle, link: '/admin/whatsapp', tint: 'bordo-dark' },
            ].map((action, i) => {
              const Icon = action.icon;
              const tintClasses: Record<string, string> = {
                'bordo': 'bg-bordo/10 border-bordo/30 hover:bg-bordo/15 text-bordo',
                'musgo': 'bg-musgo/10 border-musgo/30 hover:bg-musgo/15 text-musgo',
                'mostarda': 'bg-mostarda/20 border-mostarda/40 hover:bg-mostarda/30 text-mostarda-dark',
                'bordo-dark': 'bg-bordo-dark/10 border-bordo-dark/30 hover:bg-bordo-dark/15 text-bordo-dark',
              };
              return (
                <Link
                  key={i}
                  to={action.link}
                  className={`group relative p-4 rounded-xl border transition-all ${tintClasses[action.tint]}`}
                >
                  <Icon className="h-6 w-6 mb-2" />
                  <p className="text-sm font-bold text-tinta">{action.title}</p>
                  <ArrowRight className="absolute top-4 right-4 h-4 w-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
